import React from 'react';
import { useClientStore, useRouterStore } from 'apps/hooks';
import { Link } from 'core/routing';
import { observer } from 'mobx-react';
import {
  Paragraph,
  MenuItem,
  SvgIcon,
  Tooltip,
  Divisor,
  Avatar,
  NavTab,
  Menu,
  Box,
} from 'components';

import { NewTeam, SignOut, WorkspaceProfile, WorkspaceSettings } from 'icons';
import capitalize from 'utils/capitalize';
import WorkspaceSwitcherMenuItems from '../WorkspaceSwitcherMenuItems';

import generateSettingsLink from './generateSettingsLink';
import { NavPanelStyled } from './navPanel.styled';
import { navConfig } from './navConfig';

interface NavigationPanelProps {
  isSidebarOpen: boolean;
  toggleButton: React.ReactNode;
}

const NavigationPanel = observer(
  ({ isSidebarOpen, toggleButton }: NavigationPanelProps) => {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const clientStore = useClientStore();
    const { activeSpace } = clientStore;

    const canUpdate = activeSpace?.content?.canUpdate;
    const activeSpaceType = activeSpace?.spaceType.toLowerCase();
    const settingsLink = generateSettingsLink(activeSpaceType);
    const isInitiativeSpace = activeSpace?.spaceType === 'INITIATIVE';
    const activeSpacePath = activeSpace?.path;
    const activeSpaceTitle = activeSpace?.content?.title;
    const collapsedAvatarUrl = activeSpace?.content?.avatar?.url;

    let collapsedAvatarName = activeSpace?.content?.slug;

    if (isInitiativeSpace) {
      collapsedAvatarName = activeSpace.content.title;
    }

    const handleAvatarClick = (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
      setAnchorEl(null);
    };

    const handleSignOut = () => {
      clientStore.SignOutUser();
    };

    const { route } = useRouterStore();

    const navItems = navConfig.map(conf => {
      const isActive = conf.routes.includes(route.name);
      let params = {};

      if (conf.fillParamWithActiveSpace) {
        params = {
          [conf.fillParamWithActiveSpace]: clientStore.activeSpace?.path,
        };
      }

      let { name } = conf;
      if (conf.isRoleSensitive) {
        if (clientStore.currentPortal === 'INTERMEDIATOR') {
          name = name.replace('workspace', 'workspace.intermediator');
        }
      }

      return (
        <Link key={name} params={params} name={name}>
          <NavTab
            active={isActive}
            semiActive={isActive && isSidebarOpen}
            data-for='tabsTooltip'
            data-tip={conf.tooltip}
            data-test-id={conf.tooltip}
            data-place='right'
          >
            <SvgIcon size='xmedium' icon={conf.icon} />
          </NavTab>
        </Link>
      );
    });

    return (
      <NavPanelStyled>
        <NavTab
          onClick={handleAvatarClick}
          data-for='tabsTooltip'
          data-tip='Workspace'
          data-place='right'
        >
          <Avatar src={collapsedAvatarUrl} name={collapsedAvatarName} />
        </NavTab>
        {navItems}
        {(isInitiativeSpace && canUpdate) || !isInitiativeSpace ? (
          <Link params={{ spacePath: activeSpacePath }} name={settingsLink}>
            <NavTab
              semiActive={
                route.name.startsWith('workspace.spaceSettings') &&
                isSidebarOpen
              }
              active={route.name.startsWith('workspace.spaceSettings')}
              data-for='tabsTooltip'
              data-tip='Settings'
              data-place='right'
            >
              <SvgIcon size='medium' icon={<WorkspaceSettings />} />
            </NavTab>
          </Link>
        ) : null}

        <Divisor />
        {toggleButton}
        <Menu
          placement='overlap'
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          onClick={handleClose}
          // Specific use case for overriding FrameHeader zIndex
          sx={{ zIndex: 999999 }}
        >
          <>
            <WorkspaceSwitcherMenuItems />
            <Divisor />
            <Link name='home.newInitiative'>
              <MenuItem>
                <SvgIcon size='medium' icon={<NewTeam />} />
                <Box ml={2}>New Team Workspace</Box>
              </MenuItem>
            </Link>
            {isInitiativeSpace ? (
              <>
                <Link
                  name='initiative'
                  params={{ initiative: activeSpacePath }}
                >
                  <MenuItem>
                    <SvgIcon size='medium' icon={<WorkspaceProfile />} />
                    <Box ml={2} sx={{ display: 'flex' }}>
                      <Paragraph style={{ width: '70%' }} isTruncated={true}>
                        {capitalize(activeSpaceTitle)}
                      </Paragraph>
                      &nbsp; <span>Profile</span>
                    </Box>
                  </MenuItem>
                </Link>
              </>
            ) : (
              <>
                <Link name='profile' params={{ profile: activeSpacePath }}>
                  <MenuItem>
                    <SvgIcon size='medium' icon={<WorkspaceProfile />} />
                    <Box ml={2} sx={{ display: 'flex' }}>
                      <Paragraph style={{ width: '70%' }} isTruncated={true}>
                        {capitalize(activeSpaceTitle)}
                      </Paragraph>
                      &nbsp; <span>Profile</span>
                    </Box>
                  </MenuItem>
                </Link>
              </>
            )}
            <MenuItem onClick={handleSignOut}>
              <Box mr={2}>
                <SvgIcon size='medium' icon={<SignOut />} />
              </Box>
              Sign Out
            </MenuItem>
          </>
        </Menu>
        <Tooltip delayShow={200} place='right' id='tabsTooltip' />
      </NavPanelStyled>
    );
  },
);

export default NavigationPanel;
